// Lib
import { isFunction } from 'lodash/fp';
import Mousetrap from './Mousetrap';

// Utils
import platformSingleton from '../../platform/platformSingleton';
import { isGlobalDebugEnabled } from '../../debug/debugUtil';
import { isPlatformApple } from '../../platform/utils/platformDetailsUtils';

// Actions
import { duplicateSelectedElements } from '../../element/actions/elementShortcutActions';
import { increaseGridSize, decreaseGridSize } from '../../utils/grid/gridActions';
import { navigateBack, navigateForward, navigateToAccountSettings } from '../../reducers/navigationActions';
import { undoAction, redoAction } from '../../utils/undoRedo/undoRedoActions';
import { selectAllElements } from '../../element/selection/selectionActions';
import {
    dispatchNavigateDownHierarchy,
    dispatchNavigateUpHierarchy,
    handleSpaceShortcutAction,
} from './navigationKeyboardShortcutActions';
import {
    handleElementSecondaryReturnAction,
    startEditingSelectedElementOnReturn,
} from './editingElementKeyboardShortcutActions';
import { createNewElementOfSelectedType, toggleUnsorted } from './elementUpdateKeyboardShortcutActions';
import {
    bringForward,
    handleAlignmentShortcut,
    handleArrow,
    moveToTrash,
    moveToUnsorted,
    sendBackward,
    handleDistributionShortcut,
} from './elementMoveKeyboardShortcutActions';
import {
    deselectOrClearFilter,
    dispatchDisablePresentationMode,
    dispatchToggleTheme,
    fakeSave,
    openSearchPopup,
    shortcutStartAnnotationMode,
    toggleShortcutsModal,
    togglePresentationMode,
    zoomOut,
    zoomIn,
    zoomClear,
} from './interfaceKeyboardShortcutActions';

// Constants
import { ALIGNMENT_SIDE } from '../toolbar/components/selectionTools/arrangementTools/alignmentTools/alignmentConstants';
import { DISTRIBUTION_DIRECTION } from '../toolbar/components/selectionTools/arrangementTools/distributeTools/distributionConstants';

/**
 * Uses Mousetrap to attach keyboard shortcuts.
 * Returns a function to unbind the shortcuts.
 */
export const bindShortcuts = ({ dispatch, measurementsDispatch, currentUser }) => {
    const useDebug = isGlobalDebugEnabled();

    if (useDebug) console.groupCollapsed('%cBinding shortcuts', 'color: #74b5b4');

    const boundKeys = [];

    const bind = (keycode, dispatchAction, preventDefault = true, mousetrapInstance = Mousetrap) => {
        if (useDebug) console.info('Binding key:', keycode.join ? keycode.join(', ') : keycode);
        boundKeys.push({ keycode, mousetrapInstance });
        mousetrapInstance.bind(keycode, (event) => {
            preventDefault && event.preventDefault();

            isFunction(dispatchAction)
                ? dispatch(dispatchAction(event, { measurementsDispatch }))
                : dispatch(dispatchAction);
        });
    };

    const bindGlobal = (keycode, dispatchAction, preventDefault = true) => {
        if (useDebug) console.info('%cBinding global:', 'color: #565775', keycode.join ? keycode.join(', ') : keycode);
        return bind(keycode, dispatchAction, preventDefault, Mousetrap(document.body));
    };

    const unbind = () => {
        if (useDebug) console.groupCollapsed('%cUnbinding shortcuts', 'color: #908de0');
        boundKeys.forEach(({ keycode, mousetrapInstance }) => {
            if (useDebug) console.info('Unbinding:', keycode.join ? keycode.join(', ') : keycode);
            mousetrapInstance && mousetrapInstance.unbind(keycode);
        });
        if (useDebug) console.groupEnd('%cUnbinding shortcuts', 'color: #b59074');
    };

    const isApple = isPlatformApple(platformSingleton);

    /* ===== BIND KEYS ===== */
    // Essentials
    bind(['backspace', 'del', 'command+del', 'command+backspace'], moveToTrash);
    bind(['alt+`'], moveToUnsorted);
    bind(['up', 'shift+up'], handleArrow, false);
    bind(['down', 'shift+down'], handleArrow, false);
    bind(['left', 'shift+left'], handleArrow, false);
    bind(['right', 'shift+right'], handleArrow, false);
    bind(['command+f', 'ctrl+f'], openSearchPopup);

    // Basics
    bindGlobal(['escape', 'shift+escape'], deselectOrClearFilter);
    bindGlobal('escape', dispatchDisablePresentationMode);
    bind('return', startEditingSelectedElementOnReturn);
    bind(isApple ? 'command+z' : 'ctrl+z', undoAction);
    bind(isApple ? ['command+shift+z', 'command+y'] : ['ctrl+shift+z', 'ctrl+y'], redoAction);
    bind(isApple ? 'command+alt+p' : 'ctrl+alt+p', togglePresentationMode);
    bind(['command+a', 'ctrl+a'], selectAllElements);
    bind(['command+d', 'ctrl+d'], duplicateSelectedElements);
    bind(['command+shift+up', 'ctrl+shift+up'], bringForward);
    bind(['command+shift+down', 'ctrl+shift+down'], sendBackward);

    // Alignment
    bind('alt+w', handleAlignmentShortcut(ALIGNMENT_SIDE.TOP));
    bind('alt+d', handleAlignmentShortcut(ALIGNMENT_SIDE.RIGHT));
    bind('alt+s', handleAlignmentShortcut(ALIGNMENT_SIDE.BOTTOM));
    bind('alt+a', handleAlignmentShortcut(ALIGNMENT_SIDE.LEFT));
    bind('alt+h', handleAlignmentShortcut(ALIGNMENT_SIDE.CENTER_HORIZ));
    bind('alt+v', handleAlignmentShortcut(ALIGNMENT_SIDE.CENTER_VERT));
    // Distribution
    bind(['command+alt+v', 'ctrl+alt+v'], handleDistributionShortcut(DISTRIBUTION_DIRECTION.VERTICAL));
    bind(['command+alt+h', 'ctrl+alt+h'], handleDistributionShortcut(DISTRIBUTION_DIRECTION.HORIZONTAL));

    // Create elements
    bindGlobal(['command+return', 'ctrl+return'], createNewElementOfSelectedType);
    bindGlobal('alt+return', handleElementSecondaryReturnAction);

    // Appearance
    bindGlobal(['command+shift+,', 'ctrl+shift+,'], decreaseGridSize);
    bindGlobal(['command+shift+.', 'ctrl+shift+.'], increaseGridSize);
    bind(['shift+command+d', 'shift+ctrl+d'], dispatchToggleTheme, false);

    // Navigation
    if (!isApple) {
        bindGlobal('ctrl+[', navigateBack);
        bindGlobal('ctrl+]', navigateForward);
    }
    bind(['command+u', 'ctrl+u'], dispatchNavigateUpHierarchy, false);
    bind(['command+shift+u', 'ctrl+shift+u'], dispatchNavigateDownHierarchy, false);
    bind('space', handleSpaceShortcutAction);

    // Other
    bind(['command+\\', 'ctrl+\\'], toggleUnsorted);
    bind('/', toggleShortcutsModal);
    bind(['p', 'd'], shortcutStartAnnotationMode);
    bind('command+,', navigateToAccountSettings);

    // Hidden
    bindGlobal(['command+s', 'ctrl+s'], fakeSave);

    // NOTE: The binding of the zoom to fit "z" key is done within the ZoomToFitHandler.tsx
    bindGlobal(['command+plus', 'command+=', 'ctrl+plus', 'ctrl+='], zoomIn);
    bindGlobal(['command+-', 'ctrl+-'], zoomOut);
    bindGlobal(['command+0', 'ctrl+0'], zoomClear);

    if (useDebug) console.groupEnd('%cBinding shortcuts', 'color: #74b5b4');

    return unbind;
};
